import { UpsertEnrollmentMutationInput } from '@/store/graphql/mutations/westerosArcaneumMutations';
import { query, MicroserviceEnum } from '@/helpers/westeros';
import { formatStringToDate, formatStringToTitleCase } from '@/helpers/formatters';
import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import {
  getUFs,
  getRaces,
  getStates,
  getRgIssuers,
  getNationalities,
  OptionsQueryResult,
} from '@/store/graphql/queries/westeros-queries-options';
import {
  AcademicFormation,
  AddressInfo,
  IdentificationDocument,
  IdentificationDocumentFinancialSponsor,
  PersonalData,
  PersonalDataFinancialSponsor,
  SponsorAddressInfo,
  StudentDataState,
} from './state';
import { ONLY_LETTERS } from '@/helpers/validators';
import {
  CountyInputDTO,
  CountyQueryResult,
  getCountys,
  getCountysArgs,
} from '@/store/graphql/queries/westerosAdmissionsQueries';

const actions: ActionTree<StudentDataState, RootState> = {
  /* UPDATE ACTIONS */

  async UPDATE_PERSONAL_DATA(
    { dispatch },
    payload: { personalData: PersonalData; partnerCode: string },
  ): Promise<void> {
    const { personalData, partnerCode } = payload;

    const input: UpsertEnrollmentMutationInput = {
      student: {
        cpf: personalData.cpf,
        race: personalData.race,
        gender: personalData.gender,
        birthDate: personalData.birthDate ? formatStringToDate(personalData.birthDate) : undefined,
        nationality: personalData.nationality,
        placeOfBirth: personalData.placeOfBirth,
        householdIncome: personalData.householdIncome,
        cityBirth: personalData.cityBirth,
      },
      partnerCode: partnerCode || null,
    };

    try {
      await dispatch('UPSERT_ENROLLMENT', input);
    } catch (err) {
      const errorCode = err?.graphQLErrors?.[0]?.extensions?.exception?.status;
      const errorMessage = errorCode === 409 ? 'CPF already exists' : err?.message;

      throw new Error(errorMessage);
    }
  },

  async UPDATE_ACADEMIC_FORMATION(
    { dispatch },
    payload: { academicFormation: AcademicFormation },
  ): Promise<void> {
    const { academicFormation } = payload;

    const input: UpsertEnrollmentMutationInput = {
      student: {
        schoolCity: academicFormation.city,
        schoolCategory: academicFormation.schoolCategory,
        schoolFormationYear: academicFormation.year,
      },
    };

    await dispatch('UPSERT_ENROLLMENT', input);
  },

  async UPDATE_PERSONAL_DATA_FINANCIAL_SPONSOR(
    { dispatch },
    payload: { personalDataFinancialSponsor: PersonalDataFinancialSponsor },
  ): Promise<void> {
    const { personalDataFinancialSponsor } = payload;

    const input: UpsertEnrollmentMutationInput = {
      financialSponsor: {
        cpf: personalDataFinancialSponsor.cpf,
        email: personalDataFinancialSponsor.email,
        phone: personalDataFinancialSponsor.phone,
        fullName: personalDataFinancialSponsor.fullName?.replace(ONLY_LETTERS, ''),
        birthDate: personalDataFinancialSponsor.birthDate
          ? formatStringToDate(personalDataFinancialSponsor.birthDate)
          : undefined,
      },
    };

    await dispatch('UPSERT_ENROLLMENT', input);
  },

  async UPDATE_ADDRESS_INFO({ dispatch }, payload: { addressInfo: AddressInfo }): Promise<void> {
    const { addressInfo } = payload;

    const input: UpsertEnrollmentMutationInput = {
      student: {
        address: {
          state: addressInfo.state,
          zipCode: addressInfo.zipCode,
          streetName: addressInfo.street,
          streetNumber: addressInfo.number,
          complement: addressInfo.complement,
          neighborhood: addressInfo.neighborhood,
          city: addressInfo.city,
        },
      },
    };

    await dispatch('UPSERT_ENROLLMENT', input);
  },

  async UPDATE_SPONSOR_ADDRESS_INFO(
    { dispatch },
    payload: { sponsorAddressInfo: SponsorAddressInfo },
  ): Promise<void> {
    const { sponsorAddressInfo } = payload;

    const input: UpsertEnrollmentMutationInput = {
      financialSponsor: {
        address: {
          state: sponsorAddressInfo.sponsorState,
          zipCode: sponsorAddressInfo.sponsorZipCode,
          streetName: sponsorAddressInfo.sponsorStreet,
          streetNumber: sponsorAddressInfo.sponsorNumber,
          complement: sponsorAddressInfo.sponsorComplement,
          neighborhood: sponsorAddressInfo.sponsorNeighborhood,
          city: sponsorAddressInfo.sponsorCity,
        },
      },
    };

    await dispatch('UPSERT_ENROLLMENT', input);
  },

  async UPDATE_IDENTIFICATION_DOCUMENT(
    { dispatch },
    payload: { identificationDocument: IdentificationDocument },
  ): Promise<void> {
    const { identificationDocument } = payload;

    const input: UpsertEnrollmentMutationInput = {
      student: {
        rg: identificationDocument.rg,
        rgIssuer: identificationDocument.issuer,
        motherName: identificationDocument.motherName?.replace(ONLY_LETTERS, ''),
        fatherName: identificationDocument.fatherName?.replace(ONLY_LETTERS, ''),
        rgIssueState: identificationDocument.issueState,
        rgIssueDate: identificationDocument.issueDate
          ? formatStringToDate(identificationDocument.issueDate)
          : undefined,
      },
    };

    await dispatch('UPSERT_ENROLLMENT', input);
  },

  async UPDATE_IDENTIFICATION_DOCUMENT_FINANCIAL_SPONSOR(
    { dispatch },
    payload: { identificationDocumentFinancialSponsor: IdentificationDocumentFinancialSponsor },
  ): Promise<void> {
    const { identificationDocumentFinancialSponsor } = payload;

    const input: UpsertEnrollmentMutationInput = {
      financialSponsor: {
        rg: identificationDocumentFinancialSponsor.rg,
        rgIssuer: identificationDocumentFinancialSponsor.issuer,
        rgIssueState: identificationDocumentFinancialSponsor.issueState,
        rgIssueDate: identificationDocumentFinancialSponsor.issueDate
          ? formatStringToDate(identificationDocumentFinancialSponsor.issueDate)
          : undefined,
      },
    };

    await dispatch('UPSERT_ENROLLMENT', input);
  },

  /* PULL ACTIONS */

  async PULL_RACE_OPTIONS({ commit }): Promise<void> {
    const { data } = await query<OptionsQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getRaces,
      undefined,
      true,
    );

    const formattedOptions = data.getRaces.options?.map(formatStringToTitleCase);

    commit('SET_RACE_OPTIONS', formattedOptions);
  },

  async PULL_GENDER_OPTIONS({ commit }) {
    commit('SET_GENDER_OPTIONS', ['Feminino', 'Masculino']);
  },

  async PULL_NATIONALITY_OPTIONS({ commit }) {
    const { data } = await query<OptionsQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getNationalities,
      undefined,
      true,
    );

    commit('SET_NATIONALITY_OPTIONS', data.getNationalities.options);
  },

  // async PULL_PLACE_OF_BIRTH_OPTIONS({ commit }) {
  //   const { data } = await query<OptionsQueryResult>(
  //     MicroserviceEnum.ARCANEUM,
  //     getStates,
  //     undefined,
  //     true,
  //   );

  //   commit('SET_PLACE_OF_BIRTH_OPTIONS', data.getStates.options);
  // },

  async PULL_HOUSEHOLD_INCOME_OPTIONS({ commit }) {
    const householdIncomeOptions = [
      'Até R$ 2.000',
      'De R$ 2.000 até R$ 5.000',
      'De R$ 5.000 até R$ 8.000',
      'De R$ 8.000 até R$ 10.000',
      'Acima de R$ 10.000',
    ];
    //  TODO: pull data from API, check which values relate to lyceum's numbers.
    commit('SET_HOUSEHOLD_INCOME_OPTIONS', householdIncomeOptions);
  },

  async PULL_STATES_OPTIONS({ commit }) {
    const { data } = await query<OptionsQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getStates,
      undefined,
      true,
    );

    commit('SET_STATES_OPTIONS', data.getStates.options);
  },

  async PULL_ISSUER_OPTIONS({ commit }) {
    const { data } = await query<OptionsQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getRgIssuers,
      undefined,
      true,
    );

    commit('SET_ISSUER_OPTIONS', data.getRgIssuers.options);
  },

  async PULL_ISSUE_STATE_OPTIONS({ commit }) {
    const { data } = await query<OptionsQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getUFs,
      undefined,
      true,
    );

    commit('SET_ISSUE_STATE_OPTIONS', data.getUFs.options);
  },

  async PULL_COUNTYS_OPTIONS({ commit }) {
    try {
      commit('SET_LOADING_COUNTYS_OPTIONS', true);
      const { data } = await query<CountyQueryResult>(
        MicroserviceEnum.ARCANEUM,
        getCountys,
        undefined,
        true,
      );

      commit('SET_COUNTYS_OPTIONS', data.getCountys);
      commit('SET_COUNTY_OPTIONS_UNINFORMED');
    } finally {
      commit('SET_LOADING_COUNTYS_OPTIONS', false);
    }
  },

  async PULL_COUNTYS_ARGS_OPTIONS({ commit }, payload: CountyInputDTO) {
    try {
      commit('SET_LOADING_COUNTYS_OPTIONS', true);

      const { data } = await query<CountyQueryResult>(
        MicroserviceEnum.ARCANEUM,
        getCountysArgs,
        { args: payload },
        true,
      );

      commit('SET_COUNTYS_OPTIONS', data.getCountys);
      commit('SET_COUNTY_OPTIONS_UNINFORMED');
    } finally {
      commit('SET_LOADING_COUNTYS_OPTIONS', false);
    }
  },
};

export default actions;
